import React, { Suspense, lazy, useContext, useEffect, useRef, useState } from "react";
import { EditorInfoContext } from "../../../../../../../utils/userInfoContext";
import { NavigatorContext } from "../../../../../../../utils/navigatorContext";
import { sanitizeHtml } from "../../../../../../../utils/sanitizeHtml";
import { isPhoneLike, normalizePhone, normalizeUrlProtocol, stripLinkPrefix } from "../../../../../../../utils/helper";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import CustomTooltip from "../../../../../../Elements/Tooltip";
import KdfActionModal from "../../../../../../Modals/EditorModals/KdfActionModal";
import { networkService } from "../../../../../../config/axios";
import { loadPhoneValidation } from "../../../../../../../utils/phoneValidation";

const CtaPhoneInput = lazy(() => import("./CtaPhoneInput"));

const LINK_TYPES = [
  { value: "url", label: "URL" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

const ClickActionSection = ({
  tool,
  handleChange,
  scrollActive,
  totalPages,
  currentDocId,
  setTool,
  errorState,
  pdfId,
  toolType
}) => {
  const [openKdfModal, setOpenKdfModal] = useState(false);
  const [isJumpToFocused, setIsisJumpToFocusedFocused] = useState(false);
  const [kdfsOptions, setKdfsOptions] = useState({
    kdfList: [],
    selected: false,
  });
  const [clickOption, setClickOption] = useState([]);
  const [laserlinxOptions, setLaserLinxOptions] = useState({
    laserLinxs: [],
    selected: false,
  });
  const { elements } = useContext(EditorInfoContext);
  const { navigatorPages } = useContext(NavigatorContext);

  const navPageConfig = navigatorPages?.[tool?.page];
  const isNavigatorEnabled = navPageConfig?.status === 'approved' && navPageConfig?.enabled !== false;

  const arrayOfElements = Object.values(
    Object.entries(elements).length !== 0 && elements
  );

  // Refs for debouncing and stale request protection
  const debounceTimerRef = useRef(null);
  const requestIdRef = useRef(0);

  const linkRequestIdRef = useRef(0);

  const linkToLaserLink = (laserLink) => {
    setTool((prev) => ({
      ...prev,
      value: {
        ...prev?.value,
        link_title: laserLink?.name,
        link: laserLink?.id,
        url: laserLink?.tracking_link,
      },
    }));
    setLaserLinxOptions({ laserLinxs: [], selected: true });
  };

  const fetchlaserlinxs = async (docId, linkName = "", autoPickSingle = false) => {
    if (!docId) return;
    const requestId = ++linkRequestIdRef.current;
    const res = await networkService(
      `links/get_doc_links.json?link_name=${encodeURIComponent(linkName || "")}&doc_id=${docId}`,
      "GET"
    );
    if (requestId !== linkRequestIdRef.current) return;
    const laserLinxs = res?.data?.tracking_links || [];
    if (autoPickSingle && laserLinxs.length === 1) {
      linkToLaserLink(laserLinxs[0]);
      return;
    }
    setLaserLinxOptions({ laserLinxs, selected: false });
  };

  const linkToKdf = (kdf) => {
    setTool((prev) => ({
      ...prev,
      kdf: kdf?.title,
      value: {
        ...prev?.value,
        kdf: kdf?.id,
        kdf_title: kdf?.title_truncated,
        link: undefined,
        link_title: undefined,
        url: undefined,
        page_link: null,
      },
    }));

    setKdfsOptions({
      kdfList: [],
      selected: true,
    });

    setLaserLinxOptions({ laserLinxs: [], selected: false });

    fetchlaserlinxs(kdf?.id, "", true);
  };

  const handleLinkTitleChange = (e) => {
    handleChange(e);
    fetchlaserlinxs(tool?.value?.kdf, e.target.value);
  };

  // Custom handler for KDF title input that resets selection
  const handleKdfTitleChange = (e) => {
    // Reset selection when user types
    setKdfsOptions({
      ...kdfsOptions,
      selected: false,
    });
    handleChange(e);
  };

  // Removed automatic modal opening - modal now opens via info icon click

  useEffect(() => {
    const fetchLightBoxList = async () => {
      try {
        let list = await networkService(
          `docs/all-lightboxes/${pdfId}.json?by_trigger=no`,
          "GET"
        );
        const arrayOfLightbox = Object.values(
          Object.entries(list?.data).length !== 0 && list?.data
        );
        setClickOption(arrayOfLightbox.filter((elem) => elem.display == 'click'));
      } catch (error) {
        console.error("Error fetching lightbox list:", error);
        setLoading(false);
      }
    };
    fetchLightBoxList();
  }, []);

  useEffect(() => {
    const fetchKdfs = async (requestId) => {
      try {
        const response = await networkService(
          `kdfs.json?title=${tool?.value?.kdf_title}`,
          "GET"
        );

        // Only update state if this is still the latest request
        if (requestId === requestIdRef.current) {
          setKdfsOptions({
            kdfList: response?.data?.filter(
              (val) =>
                val?.id !== currentDocId &&
                val?.tracking_links?.some((link) => link?.kdf_action_link)
            ),
            selected: false, // Reset selection when new results arrive
          });
        }
      } catch (error) {
        console.error("Error fetching KDFs:", error);
      }
    };

    if (tool?.value?.kdf_title && tool?.value?.kdf) {
      setKdfsOptions({ kdfList: [], selected: true });
    } else if (tool?.value?.kdf_title) {
      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set up debounced search (300ms delay)
      debounceTimerRef.current = setTimeout(() => {
        requestIdRef.current += 1;
        const currentRequestId = requestIdRef.current;
        fetchKdfs(currentRequestId);
      }, 300);
    } else {
      // Clear results when input is empty
      setKdfsOptions({
        kdfList: [],
        selected: false,
      });
    }

    // Cleanup function to clear timer
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [tool?.value?.kdf_title, tool?.value?.kdf, tool?.id]);

  // Resolve the display value for the URL/email field:
  // - existing mail  → strip mailto: prefix, show plain email
  // - existing url/phone → show as-is
  // - nothing yet   → empty, so the "https://" placeholder shows through instead of real text
  const resolveUrlDisplay = (value, phone, mail) => {
    if (mail) return stripLinkPrefix(mail);
    if (value || phone) return value || phone;
    return '';
  };

  // Normalize a raw input value the same way useEditorHook does on blur,
  // so the draft reflects the final stored value immediately without waiting
  // for a tool.value change (which won't fire if the normalized URL is unchanged).
  // mailto:/tel: prefixes are stripped first (people paste links copied straight
  // out of a mail client or phone app), and a bare protocol with nothing after it
  // collapses back to empty rather than being treated as a real value.
  const normalizeForDisplay = (raw) => {
    const val = stripLinkPrefix((raw || '').trim());
    if (!val || /^https?:\/\/$/i.test(val) || val === '@') return '';
    if (val.includes('@')) return val; // email — no prefix
    if (isPhoneLike(val)) return normalizePhone(val); // phone — normalized, no prefix
    return normalizeUrlProtocol(val); // upgrades http:// to https://, repairs doubled/half-typed protocols, adds https:// to bare domains
  };

  // Local draft state: lets the user edit freely without https:// being re-injected on every keystroke.
  // We commit to handleChange on blur (typing) or immediately on paste (see commitHref/commitLjUrl below),
  // so protocol normalization runs once the value is "final" rather than on every keystroke.
  const [urlDraft, setUrlDraft] = useState(
    tool?.value?.kdf
      ? stripLinkPrefix(tool?.href || '')
      : resolveUrlDisplay(tool?.value?.url, tool?.value?.phone, tool?.value?.mail)
  );
  const [ljUrlDraft, setLjUrlDraft] = useState(
    resolveUrlDisplay(tool?.value?.lj_url, tool?.value?.lj_phone, tool?.value?.lj_mail)
  );

  // Sync draft when tool.value changes externally (e.g. KDF picker, element switch)
  useEffect(() => {
    setUrlDraft(
      tool?.value?.kdf
        ? stripLinkPrefix(tool?.href || '')
        : resolveUrlDisplay(tool?.value?.url, tool?.value?.phone, tool?.value?.mail)
    );
  }, [tool?.id, tool?.value?.url, tool?.value?.mail, tool?.value?.phone, tool?.href]);

  useEffect(() => {
    setLjUrlDraft(resolveUrlDisplay(tool?.value?.lj_url, tool?.value?.lj_phone, tool?.value?.lj_mail));
  }, [tool?.id, tool?.value?.lj_url, tool?.value?.lj_mail, tool?.value?.lj_phone]);

  const hrefStoredDisplay = () =>
    tool?.value?.kdf
      ? stripLinkPrefix(tool?.href || '')
      : resolveUrlDisplay(tool?.value?.url, tool?.value?.phone, tool?.value?.mail);
  const ljUrlStoredDisplay = () =>
    resolveUrlDisplay(tool?.value?.lj_url, tool?.value?.lj_phone, tool?.value?.lj_mail);

  // Shared commit logic for both the "Link to:" and "Link & Jump to:" fields: normalize the
  // raw value, push it to handleChange only if it actually changed, and settle the draft on
  // the normalized display value. Used on blur (typed input) and immediately on paste, so a
  // pasted link (presumed complete) gets its http:// upgraded to https:// right away instead
  // of waiting for the field to lose focus.
  const makeUrlCommitter = (fieldName, getStoredDisplay, setDraft) => (rawValue) => {
    const val = stripLinkPrefix(rawValue);
    const normalizedVal = normalizeForDisplay(val);
    const storedVal = normalizeForDisplay(getStoredDisplay());
    if (normalizedVal !== storedVal) {
      handleChange({ target: { name: fieldName, value: val, type: 'text' } });
    }
    setDraft(normalizedVal);
  };

  const commitHref = makeUrlCommitter('href', hrefStoredDisplay, setUrlDraft);
  const commitLjUrl = makeUrlCommitter('link_and_jump_url', ljUrlStoredDisplay, setLjUrlDraft);

  // Paste inserts a complete link, so normalize it immediately instead of waiting for blur.
  // React's onChange fires after the pasted text has landed in the DOM value (unlike onPaste,
  // which fires before), and the native InputEvent tells us the change came from a paste.
  const handleUrlChange = (setDraft, commit) => (e) => {
    if (e.nativeEvent?.inputType === 'insertFromPaste') {
      commit(e.target.value);
    } else {
      setDraft(stripLinkPrefix(e.target.value));
    }
  };

  const detectLinkType = (url, mail, phone) =>
    phone ? "phone" : mail ? "email" : "url";

  const [hrefType, setHrefType] = useState(
    detectLinkType(tool?.value?.url, tool?.value?.mail, tool?.value?.phone)
  );
  const [ljType, setLjType] = useState(
    detectLinkType(tool?.value?.lj_url, tool?.value?.lj_mail, tool?.value?.lj_phone)
  );

  useEffect(() => {
    loadPhoneValidation();
  }, []);

  useEffect(() => {
    setHrefType(detectLinkType(tool?.value?.url, tool?.value?.mail, tool?.value?.phone));
    setLjType(detectLinkType(tool?.value?.lj_url, tool?.value?.lj_mail, tool?.value?.lj_phone));
  }, [tool?.id]);

  useEffect(() => {
    const { url, mail, phone } = tool?.value || {};
    if (url || mail || phone) setHrefType(detectLinkType(url, mail, phone));
  }, [tool?.value?.url, tool?.value?.mail, tool?.value?.phone]);

  useEffect(() => {
    const { lj_url, lj_mail, lj_phone } = tool?.value || {};
    if (lj_url || lj_mail || lj_phone) setLjType(detectLinkType(lj_url, lj_mail, lj_phone));
  }, [tool?.value?.lj_url, tool?.value?.lj_mail, tool?.value?.lj_phone]);

  const handleHrefTypeChange = (newType) => {
    setHrefType(newType);
    setUrlDraft("");
    handleChange({ target: { name: "href", value: "", type: "text" } });
  };
  const handleLjTypeChange = (newType) => {
    setLjType(newType);
    setLjUrlDraft("");
    handleChange({ target: { name: "link_and_jump_url", value: "", type: "text" } });
  };

  const linkPlaceholders = { url: "https://", email: "name@example.com", phone: "+1 555 000 1234" };

  const renderLinkField = ({
    type, onTypeChange, fieldName, textDraft, onTextChange, onTextCommit, phoneValue, minWidth = 0,
  }) => (
    <div className="cta-link-field d-flex align-items-center" style={{ gap: "8px", flex: 1, minWidth }}>
      <select
        className="sel-clickpop cta-link-type"
        style={{ width: "88px", flex: "0 0 88px" }}
        value={type}
        onChange={(e) => onTypeChange(e.target.value)}
      >
        {LINK_TYPES.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>
      {type === "phone" && (!phoneValue || phoneValue.startsWith("+")) ? (
        <Suspense fallback={<input className="url-inp" style={{ flex: 1, minWidth: 0 }} disabled placeholder={linkPlaceholders.phone} />}>
          <CtaPhoneInput
            value={phoneValue}
            onChange={(val) => handleChange({ target: { name: fieldName, value: val, type: "text", linkType: "phone" } })}
          />
        </Suspense>
      ) : (
        <input
          className="url-inp"
          style={{ flex: 1, minWidth: 0 }}
          value={textDraft}
          onChange={onTextChange}
          onBlur={(e) => onTextCommit(e.target.value)}
          name={fieldName}
          onFocus={(e) => e.target.select()}
          placeholder={linkPlaceholders[type]}
        />
      )}
    </div>
  );


  // Console log for Jump to Page configuration tracking
  useEffect(() => {
    const isJumpToPageEnabled = tool?.action === 'page' || tool?.value?.page_link;

    if (isJumpToPageEnabled && tool?.id) {
      console.log('⚙️ Element Modal - Jump to Page Configuration:', {
        elementId: tool?.id,
        elementType: tool?.element_type_name || 'Unknown',
        onPage: tool?.page || 'Unknown',
        jumpToPage: tool?.jump_to_page || tool?.value?.page_link || 'Unknown',
        reactivateScrolling: tool?.value?.reactivate_scroll || false,
        action: tool?.action
      });
    }
  }, [tool?.action, tool?.value?.page_link, tool?.jump_to_page, tool?.id, tool?.value?.reactivate_scroll]);

  const usePicker = toolType !== 'Clone';
  const derivedAction = (() => {
    const v = tool?.value || {};
    let found = 'none';
    if (tool?.dropdown_data && Object.keys(tool.dropdown_data).length > 0) found = 'desiredaction';
    if (v.page_link) found = 'page';
    if (!v.kdf_title && (v.url || v.phone || v.mail)) found = 'href';
    if (v.lj_page_link && (v.lj_url || v.lj_phone || v.lj_mail)) found = 'link_and_jump';
    if (v.light_box) found = 'on';
    if (v.kdf_title?.length > 0) found = 'kdf';
    return found;
  })();
  const ACTION_VALUES = ['desiredaction', 'page', 'href', 'link_and_jump', 'on', 'kdf'];
  const selectedAction = ACTION_VALUES.includes(tool?.action) ? tool.action : derivedAction;

  return (
    <>
      <KdfActionModal
        openKdfModal={openKdfModal}
        closeModal={setOpenKdfModal}
      />
      <div className={`animation-select viewport-main ${JSON.stringify(tool)} ${toolType === 'Clone' ? 'cloned-action' : 'pb-4'}${usePicker ? ' ca-picker-mode' : ''}`} data-ca={selectedAction}>
        {usePicker && (
          <label className="ca-picker">
            <span>Action</span>
            <select
              className="animation-selector ca-select"
              name="action"
              value={selectedAction}
              onChange={handleChange}
            >
              <option value="none">No action</option>
              <option value="desiredaction">Hide / Reveal another element</option>
              <option value="page" disabled={isNavigatorEnabled}>Jump to page{isNavigatorEnabled ? ' (off: Navigator on this page)' : ''}</option>
              <option value="link_and_jump">Open a link and jump to page</option>
              <option value="href">Open a link (URL, email or phone)</option>
              <option value="on" disabled={clickOption.length < 1}>Reveal ClickPop{clickOption.length < 1 ? ' (no ClickPop lightboxes yet)' : ''}</option>
              <option value="kdf">Link to another KDF</option>
            </select>
          </label>
        )}

        <div className={`ca-row ca-row--none d-flex viewport-main mt-2 ${toolType === 'Clone' && (tool?.action == 'none' && (tool?.dropdown_data && Object.keys(tool.dropdown_data).length > 0)) ? 'show' : 'hidden'}`}>
          <div className="element-actions__option no-act mt-2 d-flex align-items-center">
            <div className="d-flex align-items-center">
              <input
                className="me-2"
                type="radio"
                name="action"
                value="none"
                id="none"
                checked={tool?.action == 'none'}
                onClick={(e) => handleChange(e)}
                onChange={handleChange}
              />
              <label className="img-lab-clr" htmlFor="none">
                No action
              </label>
            </div>
          </div>
        </div>
        <div className={`ca-row ca-row--desiredaction d-flex justify-content-between mt-2 mb-3 ${toolType === 'Clone' && (tool?.action === 'desiredaction' || (tool?.dropdown_data && Object.keys(tool.dropdown_data).length > 0)) ? 'show' : 'hidden'}`}>
          <div className="element-actions__option mt-1 ">
            <div className="d-flex align-items-center mb-1">
              <input
                type="radio"
                name="action"
                value="desiredaction"
                id="hide-reveal"
                checked={
                  tool?.action === "desiredaction" ||
                  (tool?.dropdown_data &&
                    Object.keys(tool.dropdown_data).length > 0)
                }
                onChange={handleChange}
              />
              <label className="img-lab-clr" htmlFor="hide-reveal">Hide / Reveal
                <CustomTooltip title={<p className="p-1 text-light">When a recipient clicks on a trigger element such as a hotspot or image, the target element will appear or disappear</p>}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 cursor-pointer" />
                </CustomTooltip>
              </label>
            </div>
            <select
              className="animation-selector"
              name="desiredaction"
              onChange={handleChange}
              defaultValue={"hide"}
              value={tool?.dropdown_data?.desiredaction}
            >
              <option value="hide">Hide</option>
              <option value="reveal">Reveal</option>
              <option value="toggleOpacity">Toggle Opacity</option>
            </select>
          </div>
          <div className="element-actions__option mt-1 ">
            <div className="d-flex mb-1">
              <label className="img-lab-clr">Target Element</label>
            </div>
            <select
              className="animation-selector"
              name="thisElement"
              onChange={handleChange}
              value={tool?.dropdown_data?.thisElement}
            >
              <option value="thisElement">
                {"P" + tool?.page} This Element
              </option>
              {arrayOfElements
                ?.filter(
                  (element) =>
                    pdfId == element?.docs_file_id &&
                    element?.element_type !== 13 &&
                    element?.geometry?.top !== null &&
                    element?.geometry?.left !== null &&
                    element?.geometry?.height !== null &&
                    element?.geometry?.top !== null &&
                    element?.id != tool?.id
                )
                .sort((a, b) => a.page - b.page)
                .map((element, index) => (
                  <option
                    value={element?.id}
                    key={index}
                    defaultValue={element?.id == tool?.dropdown_data?.thisElement}
                  >
                    {"P" + element?.page + " " + element?.element_type_name + " " + element?.id}
                  </option>
                ))}
            </select>
          </div>
          <div className="element-actions__option mt-1 ">
            <div className="d-flex mb-1">
              <label className="img-lab-clr" htmlFor="hide-reveal"> Frequency
                <CustomTooltip title={<p className="p-1 text-light">If the Frequency is set to anything other than On Every Click, you'll need to select a lightbox.
                  When someone clicks the element, the first click will trigger the intended action, like hide or reveal. For any subsequent clicks, a lightbox will appear. If you use this feature for a contest, be sure to create and assign your own hide / reveal lightbox before assigning this action to an element.</p>}>
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-2 cursor-pointer" />
                </CustomTooltip>
              </label>
            </div>
            <select
              className="animation-selector"
              name="frequency_animation"
              onChange={handleChange}
              value={tool?.dropdown_data?.frequency}
            >
              <option
                value="OnEveryClick"
              >
                On every click
              </option>
              <option
                value="oncePerKDF"
              >
                Once per KDF
              </option>
              <option
                value="oncePerLaserLINK"
              >
                Once per LaserLINK
              </option>
              <option
                value="oncePerVisit"
              >
                Once per Visit
              </option>
            </select>
          </div>
        </div>
        <div data-label="Page" className={`ca-row ca-row--page element-actions__option d-flex align-items-center mt-2 ${toolType === 'Clone' && (tool?.action === "page" || tool?.value.page_link) ? 'show' : 'hidden'}`} >
          <div className="d-flex link-to-jumpto align-items-center">
            <input
              className="me-2"
              type="radio"
              name="action"
              id="jump-action"
              value="page"
              checked={tool?.action === "page" || !!tool?.value.page_link}
              onChange={handleChange}
              disabled={isNavigatorEnabled}
            />
            <label htmlFor='jump-action' className="img-lab-clr">Jump to page:
              {isNavigatorEnabled && (
                <CustomTooltip title={<p className="p-1 text-light">Navigator is enabled on this page, so this option is disabled.</p>}>
                  <span className="ms-2 text-warning">⚠</span>
                </CustomTooltip>
              )}
            </label>
          </div>
          <div>
            <input
               type="number"
                name="jump_to_page"
                min={1}
                max={totalPages}
                value={tool?.jump_to_page ? tool?.jump_to_page:tool?.value?.page_link}
                onChange={handleChange}
                onFocus={(e) => {e.target.select()
                  setIsisJumpToFocusedFocused(true)}}
                // onBlur={() => setIsisJumpToFocusedFocused(false)}
                style={{ marginLeft: '8px' }}
                disabled={isNavigatorEnabled}
            />
            <span className="jump-span"> / {totalPages}</span>
            {usePicker && isNavigatorEnabled && (
              <span className="ca-hint"><FontAwesomeIcon icon={faInfoCircle} className="me-1" />Off: Navigator is on for this page</span>
            )}
          </div>
          <div className="d-flex align-items-center ms-4">
            <input className="me-2" type="checkbox" checked={tool?.value?.reactivate_scroll || false} name="scrolling" id="reactivate-scrolling" onChange={handleChange} />
            <label className="img-lab-clr mb-0" htmlFor="reactivate-scrolling">
              Reactivate Scrolling
            </label>
          </div>
        </div>
        {isJumpToFocused && tool.value.page_link == tool.page && <p className="text-danger">{errorState?.pageErr}</p>}
        <div data-label="Link" className={`ca-row ca-row--link_and_jump element-actions__option d-flex flex-wrap align-items-center mt-2 ${toolType === 'Clone' && (tool?.action === "link_and_jump" || (tool?.value?.lj_page_link && (tool?.value?.lj_url || tool?.value?.lj_phone || tool?.value?.lj_mail))) ? 'show' : 'hidden'}`}>
          <div className="link-to-editor align-items-center" style={{ flexShrink: 0 }}>
            <input
              type="radio"
              name="action"
              value="link_and_jump"
              id="action-link-and-jump"
              onChange={handleChange}
              checked={tool?.action === "link_and_jump" || !!(tool?.value?.lj_page_link && (tool?.value?.lj_url || tool?.value?.lj_phone || tool?.value?.lj_mail))}
            />
            <label className="img-lab-clr" htmlFor="action-link-and-jump">Link & Jump to:
              {isNavigatorEnabled && (
                <CustomTooltip title={<p className="p-1 text-light">Navigator is enabled on this page, so the jump-to-page field is disabled.</p>}>
                  <span className="ms-2 text-warning">⚠</span>
                </CustomTooltip>
              )}
            </label>
          </div>
          {renderLinkField({
            type: ljType,
            onTypeChange: handleLjTypeChange,
            fieldName: "link_and_jump_url",
            textDraft: ljUrlDraft,
            onTextChange: handleUrlChange(setLjUrlDraft, commitLjUrl),
            onTextCommit: commitLjUrl,
            phoneValue: tool?.value?.lj_phone,
            minWidth: "300px",
          })}
          <div className="d-flex align-items-center ms-auto" style={{ flexShrink: 0 }}>
            <label className="img-lab-clr ms-2 me-1" style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{usePicker ? 'Jump to' : 'Jump to:'}</label>
            <input
              type="number"
              name="link_and_jump_page"
              min={1}
              max={totalPages}
              value={tool?.value?.lj_page_link || ''}
              onChange={handleChange}
              onFocus={(e) => e.target.select()}
              style={{ width: '60px', flexShrink: 0 }}
              disabled={isNavigatorEnabled}
            />
            <span className="jump-span" style={{ flexShrink: 0 }}> / {totalPages}</span>
            {usePicker && isNavigatorEnabled && (
              <span className="ca-hint"><FontAwesomeIcon icon={faInfoCircle} className="me-1" />Off: Navigator is on for this page</span>
            )}
          </div>
        </div>
        {errorState?.ljErr && tool?.action === "link_and_jump" && <p className="text-danger">{errorState?.ljErr}</p>}
        <div data-label="Link" className={`ca-row ca-row--href element-actions__option d-flex align-items-center mt-2 ${toolType === 'Clone' && (tool?.action === "href" || (!tool?.value?.kdf_title && (tool?.value?.url || tool?.value?.phone || tool?.value?.mail))) ? 'show' : 'hidden'}`}>
          <div className="link-to-editor align-items-center">
            <input
              type="radio"
              name="action"
              value="href"
              id="action-link"
              onChange={handleChange}
              checked={tool?.action === "href" || (!tool?.value?.kdf_title && !!(tool?.value?.url || tool?.value?.phone || tool?.value?.mail))}
            />
            <label className="img-lab-clr" htmlFor="action-link">Link to:</label>
          </div>
          {renderLinkField({
            type: hrefType,
            onTypeChange: handleHrefTypeChange,
            fieldName: "href",
            textDraft: urlDraft,
            onTextChange: handleUrlChange(setUrlDraft, commitHref),
            onTextCommit: commitHref,
            phoneValue: tool?.value?.phone,
          })}
        </div>
        {errorState?.hrefErr && (tool?.action === "href" || !!(tool?.value?.url || tool?.value?.phone || tool?.value?.mail)) && (
          <p className="text-danger">{errorState.hrefErr}</p>
        )}
        <div data-label="Lightbox" className={`ca-row ca-row--on element-actions__option d-flex align-items-center mt-2 ${toolType === 'Clone' && (tool?.action === "on" || tool?.value?.light_box) ? 'show' : 'hidden'}`}>
          <div className="link-to-Reveal pt-1">
            <input
              type="radio"
              className="mt-1"
              id="reveal-clickpop"
              name="action"
              value='on'
              disabled={clickOption.length < 1}
              checked={tool?.action === "on" || tool?.value?.light_box}
              onChange={handleChange}
            />
            <label htmlFor="reveal-clickpop" className="img-lab-clr">Reveal ClickPop:</label>
          </div>
          <select
            className="element-actions-select sel-clickpop"
            name="light_box"
            disabled={clickOption?.length < 1}
            value={tool?.value?.light_box}
            onChange={handleChange}
          >
            <option value="">{clickOption.length < 1 ? 'No Lightbox Available' : 'Select a ClickPop lightbox'}</option>
            {clickOption?.map((click) => (
              <option key={click?.id} value={click?.id}>
                {click?.name}
              </option>
            ))}
          </select>
        </div>
        <div data-label="KDF" className={`ca-row ca-row--kdf element-actions__option d-flex align-items-center mt-2 ${toolType === 'Clone' && (tool?.action === "kdf" || tool?.value?.kdf_title) ? 'show' : 'hidden'}`}>
          <div className="link-to-Reveal align-items-center">
            <input
              type="radio"
              name="action"
              value="kdf"
              id="action-kdf"
              checked={
                tool?.action === "kdf" || tool?.value?.kdf_title?.length > 0
              }
              onChange={handleChange}
            />
            <label htmlFor='action-kdf' className="img-lab-clr">
              Link to KDF:
              <CustomTooltip title={<p className="p-1 text-light">Click for K2K setup instructions</p>}>
                <FontAwesomeIcon icon={faInfoCircle} className="ms-2 cursor-pointer" onClick={() => setOpenKdfModal(true)} />
              </CustomTooltip>
            </label>
          </div>
          <div className="position-relative">
            <input
              className="url-inp"
              placeholder="Enter KDF title here"
              name="kdf_title"
              value={tool?.value?.kdf_title ?? ""}
              onChange={handleKdfTitleChange}
            />
            {usePicker && (
              <CustomTooltip title={<p className="p-1 text-light mb-0">How linking to another KDF works</p>}>
                <button type="button" className="ca-help-icon" aria-label="How linking to another KDF works" onClick={() => setOpenKdfModal(true)}>
                  <FontAwesomeIcon icon={faInfoCircle} />
                </button>
              </CustomTooltip>
            )}
            {!kdfsOptions?.selected && kdfsOptions?.kdfList?.length > 0 && (
              <ul className="kdf-title-drop-ul">
                {tool?.action == "kdf" &&
                  kdfsOptions?.kdfList?.map((elem, index) => {
                    return (
                      <li
                        key={index}
                        className="cursor-pointer kdf-title-drop-li text-nowrap"
                        onClick={() => linkToKdf(elem)}
                      >
                        {elem?.title}
                      </li>
                    );
                  })}
              </ul>
            )}
          </div>
        </div>
        {kdfsOptions?.selected && (
          <div data-label="LaserLINK" className="ca-row ca-row--kdf element-actions__option d-flex align-items-center mt-2" style={{ paddingBottom: '2rem' }}>
            <div className="link-to-Reveal align-items-center">
              <label className="img-lab-clr">LaserLINK:</label>
            </div>
            <div className="position-relative">
              <input
                className="url-inp"
                placeholder="Enter Laser link title here"
                name="link_title"
                value={tool?.value?.link_title || ""}
                onChange={handleLinkTitleChange}
                onFocus={() => !tool?.value?.link && fetchlaserlinxs(tool?.value?.kdf, tool?.value?.link_title)}
              />
              {!tool?.value?.link &&
                laserlinxOptions?.laserLinxs?.length > 0 && (
                  <ul className="kdf-title-drop-ul">
                    {laserlinxOptions?.laserLinxs?.map((elem, index) => {
                      return (
                        <li
                          key={index}
                          className="cursor-pointer kdf-title-drop-li text-nowrap"
                          onClick={() => linkToLaserLink(elem)}
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(
                              elem?.name_with_language ?? "Laserlink name"
                            ),
                          }}
                        ></li>
                      );
                    })}
                  </ul>
                )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
export default ClickActionSection;
